import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  SelectChangeEvent
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

const API = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100').replace(/\/api\/v\d+\/?$/, '');

interface KitchenStation {
  id: string;
  name: string;
  code?: string;
}

interface KitchenOrder {
  id: string;
  orderNumber: string;
  kitchenStation?: KitchenStation;
  kitchenStationId?: string;
  mealType: string;
  quantity: number;
  status: string;
  requestedTime: string;
  specialInstructions?: string;
}

interface NewOrderForm {
  kitchenStationId: string;
  mealType: string;
  quantity: number;
  requestedTime: string;
  specialInstructions: string;
}

const STATUS_COLORS: Record<string, 'warning' | 'info' | 'success' | 'default'> = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'default'
};

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'];

const authHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const KitchenOrders: React.FC = () => {
  const [orders, setOrders] = useState<KitchenOrder[]>([]);
  const [stations, setStations] = useState<KitchenStation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState<NewOrderForm>({
    kitchenStationId: '',
    mealType: '',
    quantity: 1,
    requestedTime: '',
    specialInstructions: ''
  });

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/kitchen-orders`, {
        headers: authHeaders()
      });
      if (!resp.ok) throw { message: `Failed to load orders: ${resp.status}` };
      const data = await resp.json();
      const list: KitchenOrder[] = Array.isArray(data) ? data : (data.data ?? []);
      setOrders(list);
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message ?? 'Failed to load kitchen orders';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStations = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/kitchen-stations`, {
        headers: authHeaders()
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const list: KitchenStation[] = Array.isArray(data) ? data : (data.data ?? []);
      setStations(list);
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchStations();
  }, [fetchOrders, fetchStations]);

  const handleOpenDialog = (): void => {
    setForm({ kitchenStationId: '', mealType: '', quantity: 1, requestedTime: '', specialInstructions: '' });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = (): void => {
    setDialogOpen(false);
  };

  const handleSelectChange = (field: keyof NewOrderForm) => (e: SelectChangeEvent): void => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleTextChange = (field: keyof NewOrderForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const value = field === 'quantity' ? Number(e.target.value) : e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (): Promise<void> => {
    if (!form.kitchenStationId) { setFormError('Kitchen Station is required'); return; }
    if (!form.mealType) { setFormError('Meal Type is required'); return; }
    if (!form.quantity || form.quantity < 1) { setFormError('Quantity must be at least 1'); return; }
    if (!form.requestedTime) { setFormError('Requested Time is required'); return; }

    setSubmitting(true);
    setFormError(null);
    try {
      const body: Record<string, unknown> = {
        kitchenStationId: form.kitchenStationId,
        mealType: form.mealType,
        quantity: form.quantity,
        requestedTime: new Date(form.requestedTime).toISOString()
      };
      if (form.specialInstructions.trim()) {
        body.specialInstructions = form.specialInstructions.trim();
      }
      const resp = await fetch(`${API}/api/v1/kitchen-ops/kitchen-orders`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw { message: err.message ?? `Failed to create order: ${resp.status}` };
      }
      setDialogOpen(false);
      await fetchOrders();
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message ?? 'Failed to create kitchen order';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (iso: string): string => {
    if (!iso) return '--';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const stationName = (order: KitchenOrder): string => {
    if (order.kitchenStation?.name) return order.kitchenStation.name;
    const found = stations.find(s => s.id === order.kitchenStationId);
    return found?.name ?? order.kitchenStationId ?? '--';
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Kitchen Orders</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            background: 'linear-gradient(135deg, #FF6B35, #F7931E)',
            '&:hover': { background: 'linear-gradient(135deg, #F7931E, #e0841a)' }
          }}
        >
          New Kitchen Order
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : orders.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            border: '2px dashed #e0e0e0',
            borderRadius: 3,
            color: 'text.secondary'
          }}
        >
          <Typography variant="h6">No kitchen orders found</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Click "New Kitchen Order" to create the first order.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Order #</strong></TableCell>
                <TableCell><strong>Kitchen Station</strong></TableCell>
                <TableCell><strong>Meal Type</strong></TableCell>
                <TableCell><strong>Quantity</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Requested Time</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map(order => (
                <TableRow key={order.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                    {order.orderNumber ?? order.id}
                  </TableCell>
                  <TableCell>{stationName(order)}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{order.mealType}</TableCell>
                  <TableCell>{order.quantity}</TableCell>
                  <TableCell>
                    <Chip
                      label={order.status?.replace('_', ' ') ?? 'unknown'}
                      color={STATUS_COLORS[order.status] ?? 'default'}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>{formatTime(order.requestedTime)}</TableCell>
                  <TableCell>
                    {order.specialInstructions && (
                      <Typography variant="caption" color="text.secondary" title={order.specialInstructions}>
                        Has notes
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* New Order Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>New Kitchen Order</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {formError}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="station-label">Kitchen Station *</InputLabel>
            <Select
              labelId="station-label"
              label="Kitchen Station *"
              value={form.kitchenStationId}
              onChange={handleSelectChange('kitchenStationId')}
            >
              {stations.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
              {stations.length === 0 && (
                <MenuItem disabled value="">No stations available</MenuItem>
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="meal-type-label">Meal Type *</InputLabel>
            <Select
              labelId="meal-type-label"
              label="Meal Type *"
              value={form.mealType}
              onChange={handleSelectChange('mealType')}
            >
              {MEAL_TYPES.map(t => (
                <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Quantity *"
            type="number"
            value={form.quantity}
            onChange={handleTextChange('quantity')}
            inputProps={{ min: 1 }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Requested Time *"
            type="datetime-local"
            value={form.requestedTime}
            onChange={handleTextChange('requestedTime')}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Special Instructions (optional)"
            multiline
            rows={3}
            value={form.specialInstructions}
            onChange={handleTextChange('specialInstructions')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          >
            {submitting ? 'Creating...' : 'Create Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KitchenOrders;
